/**
 * /app/tasks — Per-lead follow-up reminders.
 *
 * Buckets: Overdue · Today · Upcoming · (history toggle for completed).
 * Quick-add input at the top; click any task to expand notes + edit.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CheckSquare, Square, Plus, Trash2, Clock, AlertTriangle, Calendar, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

const PRIORITY_COLOR = {
  high:   "bg-red-100 text-red-700",
  normal: "bg-emerald-50 text-emerald-700",
  low:    "bg-[var(--bg-2)] text-[var(--ink-3)]",
};

export default function Tasks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [completed, setCompleted] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: dash }, { data: comp }] = await Promise.all([
        api.get("/tasks/dashboard"),
        api.get("/tasks?only_open=false"),
      ]);
      setData(dash);
      setCompleted((comp.tasks || []).filter((t) => t.completed));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load tasks");
    }
    setLoading(false);
  }, []);

  useEffect(() => { track("tasks_page_view"); load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">Follow-ups</div>
          <h1 className="font-display font-black text-3xl tracking-tighter">Tasks</h1>
          <p className="text-[var(--ink-2)] mt-1">Stay on top of every promised callback, every check-in, every chase.</p>
        </div>
      </div>

      <QuickAdd onCreated={load} />

      {loading ? (
        <div className="card text-center py-16 text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin mx-auto mb-3" /> Loading…
        </div>
      ) : (
        <>
          <Bucket
            label="Overdue" icon={AlertTriangle} tone="red"
            tasks={data?.overdue || []} onChange={load} testid="bucket-overdue"
          />
          <Bucket
            label="Due today" icon={Calendar} tone="amber"
            tasks={data?.today || []} onChange={load} testid="bucket-today"
          />
          <Bucket
            label="Upcoming" icon={Clock} tone="emerald"
            tasks={data?.upcoming || []} onChange={load} testid="bucket-upcoming"
          />
          <div className="card p-4">
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setShowCompleted((v) => !v)}
              data-testid="tasks-toggle-completed"
            >
              {showCompleted ? "Hide" : "Show"} completed ({completed.length})
            </button>
            {showCompleted && (
              <div className="mt-3 space-y-1">
                {completed.length === 0 ? (
                  <div className="text-sm text-[var(--ink-3)]">No completed tasks yet.</div>
                ) : (
                  completed.slice(0, 50).map((t) => <TaskRow key={t.id} t={t} onChange={load} dim />)
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function QuickAdd({ onCreated }) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("normal");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const due_at = dueDate ? new Date(dueDate + "T17:00:00").toISOString() : null;
      await api.post("/tasks", { title: title.trim(), priority, due_at });
      setTitle(""); setDueDate("");
      toast.success("Task added");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add task");
    }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="card p-4 flex flex-wrap items-center gap-2" data-testid="tasks-quick-add">
      <input
        className="input flex-1 min-w-[200px]"
        placeholder="What needs doing? (e.g. Call seller about 312 Maple)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        data-testid="tasks-input-title"
      />
      <input
        type="date"
        className="input w-44"
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
        data-testid="tasks-input-due"
      />
      <select
        className="input w-32"
        value={priority}
        onChange={(e) => setPriority(e.target.value)}
        data-testid="tasks-input-priority"
      >
        <option value="low">Low</option>
        <option value="normal">Normal</option>
        <option value="high">High</option>
      </select>
      <button
        type="submit"
        className="btn-money"
        disabled={saving || !title.trim()}
        data-testid="tasks-submit"
      >
        <Plus size={14} /> Add
      </button>
    </form>
  );
}

function Bucket({ label, icon: Icon, tone, tasks, onChange, testid }) {
  const toneMap = {
    red:     "text-red-700 bg-red-50 border-l-red-500",
    amber:   "text-amber-700 bg-amber-50 border-l-amber-500",
    emerald: "text-emerald-700 bg-emerald-50 border-l-emerald-500",
  };
  return (
    <div className={`card border-l-4 p-5 ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} />
        <h2 className="font-display font-bold">{label}</h2>
        <span className="text-xs text-[var(--ink-3)]">({tasks.length})</span>
      </div>
      {tasks.length === 0 ? (
        <div className="text-sm text-[var(--ink-3)]">Nothing here. ✓</div>
      ) : (
        <div className="space-y-1">
          {tasks.map((t) => <TaskRow key={t.id} t={t} onChange={onChange} />)}
        </div>
      )}
    </div>
  );
}

function TaskRow({ t, onChange, dim }) {
  const toggle = async () => {
    try {
      await api.patch(`/tasks/${t.id}`, { completed: !t.completed });
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Deleted");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className={"flex items-start gap-3 p-3 rounded-lg bg-white border border-[var(--line)] " + (dim ? "opacity-60" : "")} data-testid={`task-${t.id}`}>
      <button type="button" onClick={toggle} className="mt-0.5 shrink-0" aria-label="Toggle complete">
        {t.completed ? <CheckSquare size={18} className="text-emerald-600" /> : <Square size={18} className="text-[var(--ink-3)]" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={"text-sm font-medium leading-tight " + (t.completed ? "line-through text-[var(--ink-3)]" : "text-[var(--ink)]")}>
          {t.title}
        </div>
        <div className="text-xs text-[var(--ink-3)] mt-1 flex flex-wrap items-center gap-2">
          {t.lead_address && <span className="truncate max-w-[200px]">📍 {t.lead_address}</span>}
          {t.due_at && <span>📅 {new Date(t.due_at).toLocaleDateString()}</span>}
          <span className={"inline-block px-1.5 py-0.5 rounded text-[10px] uppercase font-bold " + (PRIORITY_COLOR[t.priority] || PRIORITY_COLOR.normal)}>
            {t.priority}
          </span>
        </div>
      </div>
      <button type="button" onClick={remove} className="btn-ghost text-red-600 shrink-0" aria-label="Delete">
        <Trash2 size={13} />
      </button>
    </div>
  );
}
